import React, { useState, useEffect } from 'react';
import { Table, Tag, AutoComplete, Input, Form, InputNumber, message, Select, Button, Modal } from 'antd';
import { Api_VariavelGlobal } from '../global';
import { Link } from 'react-router-dom';

const { Option } = Select;

const ProdutosTodos = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Com Estoque');
  const [currentPage, setCurrentPage] = useState(1);

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editConfirmLoading, setEditConfirmLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${Api_VariavelGlobal}/api/produtos_todos`);
      const produtos = await response.json();

      const produtosFormatados = produtos
        .map((produto) => ({
          key: produto.ID_PRODUTO,
          id_produto: produto.ID_PRODUTO,
          produto: produto.PRODUTO,
          estoque_atual: produto.ESTOQUE_ATUAL,
          estoque_minimo: produto.ESTOQUE_MINIMO,
          descricao: produto.DESCRICAO,
          preco_anterior: produto.PRECO_ANTERIOR,
          preco_atual: produto.PRECO_ATUAL,
          id_categoria: produto.ID_CATEGORIA,
          sub_categoria: produto.SUB_CATEGORIA,
          status: produto.STATUS,
          imagem: produto.imageUrl,
        }))
        .sort((a, b) => b.status - a.status);

      setData(produtosFormatados);
      applyFilters(produtosFormatados, searchValue, statusFilter);
    } catch (error) {
      console.error('Erro ao buscar os produtos:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    applyFilters(data, value, statusFilter);
  };

  const onSelect = (value) => {
    setSearchValue(value);
    applyFilters(data, value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    applyFilters(data, searchValue, value);
  };

  const applyFilters = (lista, searchText, status) => {
    let filtered = [...lista];

    if (searchText) {
      filtered = filtered.filter((produto) =>
        produto.produto.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (status === 'Ativos') filtered = filtered.filter((p) => p.status === 1);
    else if (status === 'Inativos') filtered = filtered.filter((p) => p.status === 0);
    else if (status === 'Sem Estoque') filtered = filtered.filter((p) => p.estoque_atual <= 0);
    else if (status === 'Com Estoque') filtered = filtered.filter((p) => p.estoque_atual > 0);

    setFilteredData(filtered);

    setOptions(
      filtered.map((produto) => ({
        value: produto.produto,
        label: `Produto: ${produto.produto}`,
      }))
    );

    setCurrentPage(1); // Sempre volta para a primeira página ao filtrar
  };

  const showEditModal = (produto) => {
    setSelectedProduct(produto);
    setIsEditModalVisible(true);

    editForm.setFieldsValue({
      produto: produto.produto,
      estoque_atual: produto.estoque_atual,
      estoque_minimo: produto.estoque_minimo ?? 0,
      descricao: produto.descricao || '',
      preco_anterior: produto.preco_anterior,
      preco_atual: produto.preco_atual,
      id_categoria: produto.id_categoria,
      sub_categoria: produto.sub_categoria || '',
      status: produto.status,
    });
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedProduct(null);
    editForm.resetFields();
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setEditConfirmLoading(true);

      const response = await fetch(
        `${Api_VariavelGlobal}/api/produtos_update/${selectedProduct.id_produto}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            produto: values.produto,
            descricao: values.descricao || '',
            preco_anterior: Number(values.preco_anterior) || 0,
            preco_atual: Number(values.preco_atual) || 0,
            id_categoria: values.id_categoria,
            sub_categoria: values.sub_categoria || '',
            status: values.status,
            estoque_atual: Number(values.estoque_atual) || 0,
            estoque_minimo: Number(values.estoque_minimo) || 0,
          }),
        }
      );

      if (response.ok) {
        message.success('Produto atualizado com sucesso!');
        fetchData();
        handleEditModalCancel();
      } else {
        message.error('Erro ao atualizar o produto.');
      }
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar o produto.');
    } finally {
      setEditConfirmLoading(false);
    }
  };

  const atualizarEstoque = async (produto, novoEstoque) => {
    try {
      const response = await fetch(
        `${Api_VariavelGlobal}/api/produtos_update/${produto.id_produto}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            produto: produto.produto,
            descricao: produto.descricao || '',
            preco_anterior: produto.preco_anterior,
            preco_atual: produto.preco_atual,
            id_categoria: produto.id_categoria,
            sub_categoria: produto.sub_categoria || '',
            status: produto.status,
            estoque_atual: novoEstoque,
            estoque_minimo: produto.estoque_minimo || 0,
          }),
        }
      );

      if (!response.ok) {
        message.error('Erro ao atualizar estoque!');
        return;
      }

      // Atualiza na tabela sem recarregar tudo
      setData((prevData) =>
        prevData.map((item) =>
          item.id_produto === produto.id_produto
            ? { ...item, estoque_atual: novoEstoque }
            : item
        )
      );

      setFilteredData((prevData) =>
        prevData.map((item) =>
          item.id_produto === produto.id_produto
            ? { ...item, estoque_atual: novoEstoque }
            : item
        )
      );

      message.success('Estoque atualizado!');
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar o estoque.');
    }
  };

  const columns = [
    {
      title: 'Produto',
      dataIndex: 'produto',
      key: 'produto',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {record.imagem ? (
            <img
              src={`https://gestor.bebilogo.com.br/api/uploads/${record.imagem}`}
              alt={record.produto}
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <div style={{ width: 40, height: 40, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
          )}
          <span>{record.produto}</span>
        </div>
      ),
    },
    {
      title: 'Estoque Atual',
      dataIndex: 'estoque_atual',
      key: 'estoque_atual',
      render: (estoque, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            type="primary"
            danger
            size="small"
            onClick={() => atualizarEstoque(record, record.estoque_atual - 1)}
            disabled={record.estoque_atual <= 0}
          >
            -
          </Button>
          <strong style={{ minWidth: 30, textAlign: 'center' }}>{estoque}</strong>
          <Button
            type="primary"
            size="small"
            onClick={() => atualizarEstoque(record, record.estoque_atual + 1)}
          >
            +
          </Button>
        </div>
      ),
    },
    {
      title: 'Categoria',
      dataIndex: 'id_categoria',
      key: 'id_categoria',
    },
    {
      title: 'Preço Custo',
      dataIndex: 'preco_anterior',
      key: 'preco_anterior',
      render: (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`,
    },
    {
      title: 'Preço Venda',
      dataIndex: 'preco_atual',
      key: 'preco_atual',
      render: (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`,
    },
    {
      title: 'Margem de Lucro',
      key: 'margem_lucro',
      render: (_, record) => {
        const custo = Number(record.preco_anterior);
        const venda = Number(record.preco_atual);
        if (isNaN(custo) || isNaN(venda) || custo <= 0) return '0%';
        return `${(((venda - custo) / custo) * 100).toFixed(2).replace('.', ',')}%`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 1 ? 'blue' : 'red'}>{status === 1 ? 'Ativo' : 'Inativo'}</Tag>,
    },
    {
      title: 'Ação',
      key: 'acao',
      render: (_, record) => <Button type="link" onClick={() => showEditModal(record)}>Editar</Button>,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <AutoComplete
          popupMatchSelectWidth={252}
          style={{ width: 300 }}
          value={searchValue}
          options={options}
          onSelect={onSelect}
          onSearch={handleSearch}
          size="large"
        >
          <Input.Search size="large" placeholder="Buscar por Produto" enterButton />
        </AutoComplete>

        <Select
          value={statusFilter}
          onChange={handleStatusFilterChange}
          style={{ width: 200 }}
          size="large"
        >
          <Option value="Com Estoque">Com Estoque</Option>
          <Option value="Sem Estoque">Sem Estoque</Option>
          <Option value="Todos">Todos</Option>
          <Option value="Ativos">Ativos</Option>
          <Option value="Inativos">Inativos</Option>
        </Select>

        <Link to="/produtos">
          <Button style={{ width: 200 }} size="large">Produtos</Button>
        </Link>

        <Link to="/sugestao">
          <Button style={{ width: 200 }} size="large">Sugestão</Button>
        </Link>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          pageSize: 10,
          total: filteredData.length, // ⬅️ essencial para paginação
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: true,
          pageSizeOptions: ['5','10','20','50'],
        }}
        rowClassName="cursor-pointer"
        size="small"
      />

      {/* MODAL EDITAR PRODUTO */}
      <Modal
        title="Editar Produto"
        open={isEditModalVisible}
        onCancel={handleEditModalCancel}
        onOk={handleEditSave}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={editConfirmLoading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="produto" label="Produto" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="estoque_atual" label="Estoque Atual" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item name="estoque_minimo" label="Estoque Mínimo" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item name="descricao" label="Descrição">
            <Input />
          </Form.Item>

          <Form.Item name="id_categoria" label="Categoria" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="sub_categoria" label="Subcategoria">
            <Input />
          </Form.Item>

          <Form.Item name="preco_anterior" label="Preço Custo" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>

          <Form.Item name="preco_atual" label="Preço Venda" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} max={1} />
          </Form.Item>
        </Form>

        <Button
          type="primary"
          danger
          style={{ marginTop: 20 }}
          onClick={() => {
            Modal.confirm({
              title: 'Tem certeza que deseja apagar este produto?',
              content: 'Esta ação não poderá ser desfeita.',
              okText: 'Sim, apagar',
              okType: 'danger',
              cancelText: 'Cancelar',
              onOk: async () => {
                try {
                  const response = await fetch(
                    `${Api_VariavelGlobal}/api/produtos/${selectedProduct.id_produto}`,
                    { method: 'DELETE' }
                  );

                  if (response.ok) {
                    message.success('Produto apagado com sucesso!');
                    fetchData();
                    handleEditModalCancel();
                  } else {
                    message.error('Erro ao apagar o produto.');
                  }
                } catch (error) {
                  console.error('Erro ao apagar produto:', error);
                  message.error('Erro ao apagar o produto.');
                }
              },
            });
          }}
        >
          Deletar Produto
        </Button>
      </Modal>
    </>
  );
};

export default ProdutosTodos;
